import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useHttpClient } from '~/api/base';
import { Page, PageSettingOptions } from '~/api/types';
import {
  EmailDetailsResponse,
  EmailRefType,
  EmailThreadResponse,
  ReplyEmailRequest,
  SendEmailRequest
} from '../models/email';

const BASE_URL = '/emails';

export const useEmails = ({
  refType,
  refId,
  search,
  enabled,
  pageSetting
}: {
  refType: EmailRefType;
  refId: string;
  search?: string;
  enabled?: boolean;
} & PageSettingOptions): UseQueryResult<Page<EmailThreadResponse>, unknown> => {
  const client = useHttpClient(BASE_URL);
  return useQuery(
    ['emails', refType, refId, pageSetting, search],
    async () => {
      const { data } = await client.get<Page<EmailThreadResponse>>('', {
        params: { refType, refId, ...pageSetting, search }
      });
      return data;
    },
    { enabled, keepPreviousData: true }
  );
};

export const useEmail = ({
  refType,
  refId,
  id,
  enabled
}: {
  refType: EmailRefType;
  refId: string;
  id: string;
  enabled?: boolean;
}): UseQueryResult<EmailDetailsResponse, unknown> => {
  const client = useHttpClient(BASE_URL);
  return useQuery(
    ['emails', refType, refId, id],
    async () => {
      const { data } = await client.get<EmailDetailsResponse>(id, { params: { refType, refId } });
      return data;
    },
    { enabled }
  );
};

export function useSendEmail(): UseMutationResult<void, unknown, SendEmailRequest, unknown> {
  const client = useHttpClient(BASE_URL);
  const queryClient = useQueryClient();
  return useMutation(
    async (request) => {
      const { data } = await client.post<void>('', request);
      return data;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['emails']);
      }
    }
  );
}

export function useReplyEmail(): UseMutationResult<void, unknown, ReplyEmailRequest, unknown> {
  const client = useHttpClient(BASE_URL);
  const queryClient = useQueryClient();
  return useMutation(
    async (request) => {
      const { data } = await client.post<void>('reply', request);
      return data;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['emails']);
      }
    }
  );
}

export function useForwardEmail(): UseMutationResult<void, unknown, ReplyEmailRequest, unknown> {
  const client = useHttpClient(BASE_URL);
  const queryClient = useQueryClient();
  return useMutation(
    async (request) => {
      const { data } = await client.post<void>('forward', request);
      return data;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['emails']);
      }
    }
  );
}
